import type {
  AnomalyTrendData,
  AnomalyTrendQueryParams,
  EventDetailData,
  EventListData,
  EventListQueryParams,
  FacilityLocationData,
  MapPoiData,
  MapPoiQueryParams,
  SiteWeatherData,
  TrafficVolumeTrendData,
  TrafficVolumeTrendQueryParams,
} from '@/domains/traffic-monitoring/types/smart-city.types';
import type { ApiResponse } from '@/shared/types/abstract-api-response';
import { request } from '@/shared/utils/request';

const buildQuery = (params?: Record<string, unknown>): string => {
  const search = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        search.append(key, String(value));
      }
    });
  }
  return search.toString();
};

export const smartCityApi = {
  // GET /api/dsh/map/poi -> { generated_at, counts, layers{...}, degraded }
  getMapPoi: async (params?: MapPoiQueryParams): Promise<ApiResponse<MapPoiData>> => {
    const qs = buildQuery(params as Record<string, unknown> | undefined);
    return request.get<ApiResponse<MapPoiData>>(qs ? `/map/poi?${qs}` : '/map/poi');
  },

  // GET /api/dsh/map/facility/location -> { cctv, healthcare, police, train_station, wifi, traffic_light, government, fire_station }
  // PSC_PC0201001_BE_001: static facility-layer coordinates + counts.
  getMapFacilityLocation: async (): Promise<ApiResponse<FacilityLocationData>> => {
    return request.get<ApiResponse<FacilityLocationData>>('/map/facility/location');
  },

  // GET /api/dsh/event -> { date, timezone, page, size, total_count, counts_by_type, events[] }
  // PSC_PC0201001_BE_003: city-wide, merged anomaly windows + PSC reports.
  getEventList: async (params: EventListQueryParams): Promise<ApiResponse<EventListData>> => {
    const qs = buildQuery(params as unknown as Record<string, unknown>);
    return request.get<ApiResponse<EventListData>>(`/event?${qs}`);
  },

  // GET /api/dsh/event/:type/:id -> { id, type, title, location, cctv_id, cctv_name, latitude, longitude, image_path, bbox, frame_size, zones, timestamp, status? }
  // PSC_PC0201001_BE_003 companion: full detail for one Event List row, for the event-details screen.
  getEventDetail: async (
    type: string,
    id: string,
    lang?: 'eng' | 'ind' | 'kor'
  ): Promise<ApiResponse<EventDetailData>> => {
    const qs = buildQuery(lang ? { lang } : undefined);
    return request.get<ApiResponse<EventDetailData>>(
      `/event/${encodeURIComponent(type)}/${encodeURIComponent(id)}${qs ? `?${qs}` : ''}`
    );
  },

  // GET /api/dsh/site/:siteId/weather -> { site_id, name, location, coordinates, current, forecast }
  getSiteWeather: async (
    siteId: number | string,
    forecastLimit = 24
  ): Promise<ApiResponse<SiteWeatherData | null>> => {
    return request.get<ApiResponse<SiteWeatherData | null>>(
      `/site/${siteId}/weather?forecast_limit=${forecastLimit}`
    );
  },

  // GET /api/dsh/dashboard/anomaly/trend -> { inteval, time_zone, from, to, buckets[] }
  getAnomalyTrend: async (
    params?: AnomalyTrendQueryParams
  ): Promise<ApiResponse<AnomalyTrendData>> => {
    const qs = buildQuery(params as Record<string, unknown> | undefined);
    return request.get<ApiResponse<AnomalyTrendData>>(
      qs ? `/dashboard/anomaly/trend?${qs}` : '/dashboard/anomaly/trend'
    );
  },

  // GET /api/dsh/traffic-volume/trend -> { hours, interval, timezone, from, to, total_volume, times[], series[] } (series grouped by vehicle type)
  getTrafficVolumeTrend: async (
    params?: TrafficVolumeTrendQueryParams
  ): Promise<ApiResponse<TrafficVolumeTrendData>> => {
    const qs = buildQuery(params as Record<string, unknown> | undefined);
    return request.get<ApiResponse<TrafficVolumeTrendData>>(
      qs ? `/traffic-volume/trend?${qs}` : '/traffic-volume/trend'
    );
  },
};
